import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = '/api';
const AnalyticsContext = createContext(null);

export function AnalyticsProvider({ children, visitorId }) {
  const startTime = useRef(Date.now());
  const sectionTimers = useRef({});
  const lastScrollDepth = useRef(0);

  const track = useCallback(async (eventType, section = null, data = null) => {
    try {
      await axios.post(`${API}/analytics/track`, {
        visitor_id: visitorId || "anonymous",
        event_type: eventType,
        section,
        data,
      });
    } catch (e) {
      // Silent fail for analytics
    }
  }, [visitorId]);

  const trackSectionView = useCallback((section) => {
    track("section_view", section);
    sectionTimers.current[section] = Date.now();
  }, [track]);

  const trackSectionLeave = useCallback((section) => {
    const entered = sectionTimers.current[section];
    if (entered) {
      const duration = Math.round((Date.now() - entered) / 1000);
      track("section_time", section, { duration });
      delete sectionTimers.current[section];
    }
  }, [track]);

  const trackClick = useCallback((element, section = null) => {
    track("click", section, { element, timestamp: Date.now() });
  }, [track]);

  const trackDocumentView = useCallback((docName, pageNum = null) => {
    track("document_view", "documents", { item: docName, page: pageNum });
  }, [track]);

  const trackGalleryView = useCallback((imageName) => {
    track("gallery_view", "gallery", { item: imageName });
  }, [track]);

  const trackVideoPlay = useCallback((videoName, duration = null) => {
    track("video_play", "gallery", { item: videoName, duration });
  }, [track]);

  useEffect(() => {
    track("page_load", null, { url: window.location.href });

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > lastScrollDepth.current + 10) {
        lastScrollDepth.current = scrollPercent;
        track("scroll_depth", null, { depth: scrollPercent });
      }
    };

    const handleBeforeUnload = () => {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      navigator.sendBeacon(
        `${API}/analytics/track`,
        JSON.stringify({
          visitor_id: visitorId || "anonymous",
          event_type: "session_duration",
          data: { duration },
        })
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [track, visitorId]);

  return (
    <AnalyticsContext.Provider value={{ track, trackSectionView, trackSectionLeave, trackClick, trackDocumentView, trackGalleryView, trackVideoPlay }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

export function useSectionTracking(sectionName) {
  const analytics = useAnalytics();
  const ref = useRef(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!analytics || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTracked.current) {
          hasTracked.current = true;
          analytics.trackSectionView(sectionName);
        } else if (!entry.isIntersecting && hasTracked.current) {
          analytics.trackSectionLeave(sectionName);
          hasTracked.current = false;
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [analytics, sectionName]);

  return ref;
}
